import { Box, Card, CardContent, Typography } from "@mui/material";
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import { TimeRange } from "../../../../../helpers/dateRangeHelpers";
import {
  ChartDataPoint,
  SleepStagesChartDataPoint,
} from "../../../../../helpers/sleepDataHelpers";

interface SleepChartCardProps {
  title: string;
  data: ChartDataPoint[] | SleepStagesChartDataPoint[];
  colors: { primary: { main: string } };
  selectedTimeRange: TimeRange;
  dataKey?: string;
  barColor?: string;
  isStacked?: boolean;
  icon?: React.ReactNode;
}

const SleepChartCard: React.FC<SleepChartCardProps> = ({
  title,
  data,
  colors,
  selectedTimeRange,
  dataKey,
  barColor,
  isStacked = false,
  icon,
}) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "1px solid #C6C5D5",
        p: 2,
        height: 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {icon}
          <Typography
            variant="body1"
            fontWeight={500}
            color="text.primary"
            sx={{ fontSize: "0.875rem" }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, width: "100%", mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#E8E8E9" strokeWidth={1} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                interval={selectedTimeRange === "1M" ? 6 : 0}
                style={{ fontSize: "11px" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                style={{ fontSize: "11px" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderColor: "#E8E8E9",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                formatter={(value: number, name: string) => {
                  const nameMap: Record<string, string> = {
                    value: title,
                    deep: "Deep",
                    rem: "REM",
                    light: "Light",
                    awake: "Awake",
                  };
                  return [`${Math.round(value)} min`, nameMap[name] || name];
                }}
              />
              {isStacked ? (
                <>
                  <Bar
                    dataKey="deep"
                    stackId="a"
                    fill="#3B82F6"
                    stroke="#E8E8E9"
                    strokeWidth={1}
                    maxBarSize={
                      selectedTimeRange === "1D" ||
                      selectedTimeRange === "CUSTOM"
                        ? 40
                        : undefined
                    }
                  />
                  <Bar
                    dataKey="rem"
                    stackId="a"
                    fill="#8B5CF6"
                    stroke="#E8E8E9"
                    strokeWidth={1}
                    maxBarSize={
                      selectedTimeRange === "1D" ||
                      selectedTimeRange === "CUSTOM"
                        ? 40
                        : undefined
                    }
                  />
                  <Bar
                    dataKey="light"
                    stackId="a"
                    fill="#10B981"
                    stroke="#E8E8E9"
                    strokeWidth={1}
                    maxBarSize={
                      selectedTimeRange === "1D" ||
                      selectedTimeRange === "CUSTOM"
                        ? 40
                        : undefined
                    }
                  />
                  <Bar
                    dataKey="awake"
                    stackId="a"
                    fill="#F59E0B"
                    stroke="#E8E8E9"
                    strokeWidth={1}
                    maxBarSize={
                      selectedTimeRange === "1D" ||
                      selectedTimeRange === "CUSTOM"
                        ? 40
                        : undefined
                    }
                  />
                </>
              ) : (
                <Bar
                  dataKey={dataKey || "value"}
                  fill={barColor || colors.primary.main}
                  stroke="#E8E8E9"
                  strokeWidth={1}
                  maxBarSize={
                    selectedTimeRange === "1D" || selectedTimeRange === "CUSTOM"
                      ? 40
                      : undefined
                  }
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend for stacked chart */}
        {isStacked && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            {[
              { label: "Deep", color: "#3B82F6" },
              { label: "REM", color: "#8B5CF6" },
              { label: "Light", color: "#10B981" },
              { label: "Awake", color: "#F59E0B" },
            ].map(({ label, color }) => (
              <Box
                key={label}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: color,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SleepChartCard;
